import _ from 'lodash'
import Backbone from 'backbone'
import calendars from 'scoreboard/components/calendars'
import CalendarRangeSelector from 'scoreboard/components/calendar-range-selector'
import conductor from 'scoreboard/components/conductor'
import i18n from 'scoreboard/components/i18n'
import FormView from 'scoreboard/components/form-view'
import LabeledToggle from 'scoreboard/components/labeled-toggle'
import MarkedField from 'scoreboard/components/marked-field'
import MultiPrincipalChooser from 'scoreboard/components/multi-principal-chooser'
import periodSelectorDisplayed from 'scoreboard/components/period-selector-displayed'
import requiredValidator from 'scoreboard/components/validators/required-validator'
import SchedulePicker from 'scoreboard/components/schedule-picker'
import unwrapModel from 'scoreboard/components/unwrap-model'
import user from 'scoreboard/components/user'
import template from './export-form.hbs'

// cache of potential members.  keep it outside individual ExportForm instances.
// Note this is cleared out in the remove method of the view so navigating to a
// new screen always clears the cache
let potentialPrincipals

/**
 * Allows users to create a schedule for an export.
 * Alerts will be generated for each recipient at the selected time.
 */
const ExportForm = Backbone.View.extend({

	tagName: 'form',

	className: 'export-form',

	// this.model is ScheduledExport.kt
	/**
	 * @param {Principal[]} [this.model.recipients] (see Principal.java)
	 * @param {Object} [this.model.scheduleData] (see ScheduleData.kt)
	 * @param {Object} [this.model.singlePeriod] (see SinglePeriod.kt)
	 *
	 * @param {Object} options send-to-export options are passed through
	 * @param {String} [options.section] e.g. 'reports'
	 * @param {String} [options.type] e.g. 'report'
	 * @param {String} [options.subtype] e.g. 'annual-metric-data'
	 * @param {String} options.fileType
	 *
	 * @param {Boolean} [options.showToggle=true]
	 *
	 * @param {String} [options.triggerType='download']
	 * 	The toggle value to preselect: 'download' or 'schedule'
	 *
	 * @param {String} [options.instructions] Provide context specific export instructions
	 */
	initialize(options = {}) {
		this.options = _.extend({showToggle: true}, options)
		this.count = this.options.count
		this.type = this.options.triggerType || 'download'
		this.fileType = this.options.fileType

		const attr = _.get(this.model, 'attributes', {})
		this.scheduleData = attr.scheduleData || {}
		// #24974 If there are no recipients it means a new scheduled export is
		// being created, add the current user as the default
		const currentUser = () => [_.extend(
			{type:'user'},
			unwrapModel(user.model)
		)]
		this.recipients = attr.recipients || currentUser()

		this.formView = new FormView({
			el: this.el,
			submit: this.submit.bind(this),
			allowCleanSubmission: true,
			guard: false
		})
	},

	render: function() {
		const title = i18n.get('common.export.to', i18n.get(`common.export.${this.fileType}`))

		let instructions = this.options.instructions
		if (!instructions) {
			const instructionCode = 'common.export.scheduledExportInstructions'
			instructions = i18n.get(instructionCode)
			if (this.count > 0) {
				const adminText = i18n.get('admin.navigation', 'common.export.scheduledExports')
				const adminLink = `<span ` +
					`class="text-link sp-navigate" ` +
					`href="/admin/scheduled-exports">${adminText}</span>`
				instructions += ` ${i18n.get(`${instructionCode}.count`, this.count, adminLink)}`
			}
		}

		this.$el.empty().append(template({title, instructions}))

		if (this.options.showToggle) {
			this.$('.type-toggle').removeClass('is-not-displayed')
			this._renderToggle()
		}

		if (this.options.showToggle || this.options.instructions) {
			this.$('.banner').removeClass('is-not-displayed')
		}

		this._renderPeriodSelector()
		this._renderPrincipalChooser()
		this._renderSchedulePicker()
		this.formView.start()
		this.changeDisplay()
		return this
	},

	_renderToggle() {
		this.toggle = new LabeledToggle({
			value: this.type,
			options: [ {
				value: 'download',
				label: 'common.export.download'
			}, {
				value: 'schedule',
				label: 'common.export.schedule'
			} ]
		})
		this.toggle.on('change', function() {
			this.changeDisplay()
			this.formView.change()
		}.bind(this))

		this.$('.display-toggle').append(this.toggle.el)
		this.toggle.render()
	},

	_renderPeriodSelector() {

		const {section = conductor.section(), type, subtype} = this.options
		const {singlePeriod} = unwrapModel(this.model) || {}

		if (section === 'admin' && !singlePeriod) {
			// User is editing a scheduled export with no period
			return
		}

		// https://github.com/SpiderStrategies/Scoreboard/pull/23645/files#r273684455
		const range = singlePeriod || {calendarId: calendars.calendars[0].id, relative: true, first:0}
		// If these don't match the CalendarRangeSelector will not render the first value correctly
		range.last = range.first

		if (periodSelectorDisplayed({section, type, subtype})) {
			this.$('.calendar-period').removeClass('is-not-displayed')
			this.periodSelector = new CalendarRangeSelector({
				allowCurrentCalendar: false,
				canChangeCalendar : true,
				singlePeriod: true,
				range
			})
			this.periodSelector.setElement(this.$('.period-toggle')).render()
		}
	},

	_renderPrincipalChooser() {
		this.principalChooser = new MultiPrincipalChooser({
			el: this.$('.recipient-chooser'),
			cache: potentialPrincipals,
			currentPrincipals: this.recipients,
			multiple: true,
			autofocus: false
		}).render()

		// Sift out currently selected recipients
		for (const recipient of this.recipients) {
			this.principalChooser.excludePrincipal(recipient)
		}

		this.principalChooser.on('load', data => {
			potentialPrincipals = data
		})
		this.recipientField = new MarkedField({
			el: this.$('.recipients'),
			validator: [requiredValidator]
		}).render()
	},

	_renderSchedulePicker() {
		this.schedulePicker = new SchedulePicker({
			el: this.$('.schedule-picker'),
			model: this.model, // attributes req'd: timeZone
			limitInterval: SchedulePicker.intervals.DAY,
			data: this.scheduleData
		}).render()
	},

	/**
	 * Callback when the .display-toggle switches between default and custom
	 */
	changeDisplay: function() {
		_.invoke(this.recipientField, 'clearError')
		this.type = _.result(this.toggle, 'val', this.type)
		const showSchedule = this.type === 'schedule'
		const isEdit = _.get(this.model, 'id')
		this.$('.button-primary').text(i18n.get(`common.${isEdit ? 'save' : this.type}`))
		this.$('.schedule').toggleClass('is-not-displayed', !showSchedule)
		// Defer calling show so that the recipients text-area is sized correctly
		showSchedule && this.principalChooser.show({open: false}) // Idempotent for multi-select
	},

	submit: function() {
		const options = {}
		if (this.type === 'schedule') {
			options.singlePeriod = this.periodSelector && this.periodSelector.val()
			options.scheduleData = this.schedulePicker.readFromDom()
			options.recipients = _.map(this.$('select.recipients option:selected'), option => {
				return {
					id: option.value,
					type: option.dataset.type
				}
			})
		}
		this.trigger(this.type, options)
	},

	remove: function() {
		Backbone.View.prototype.remove.apply(this, arguments)
		_.invokeMap([this.toggle, this.schedulePicker,
			this.principalChooser, this.recipientField, this.periodSelector], 'remove')
		potentialPrincipals = null
	}
})

export default ExportForm
ExportForm.__filename__ = 'components/export/export-form.js'
